// 车辆EMC测试报告专用处理逻辑
import { postFormData, postJson } from './apiClient';
import { STEP, DOCUMENT_TYPE, NO_LEVEL_KEY } from './workflowConfig';
import { findMatchedTableByDirectory, getDirectoryLabel, hasRows } from '../utils/matchers';
import { normalizeTables } from './tableNormalization';

const resolveNextStepBySingleMatch = (directories, tables, setSelectedDirectory, setTableHtml) => {
  // 当目录仅匹配一个表格时自动跳转到配置步骤。
  const matchedPairs = directories
    .map((directoryItem) => {
      const directory = getDirectoryLabel(directoryItem);
      const matchedTable = findMatchedTableByDirectory(directory, tables);
      return { directory, matchedTable };
    })
    .filter((item) => item.matchedTable && item.matchedTable.html);

  if (matchedPairs.length !== 1) {
    return STEP.SELECT_DIRECTORY;
  }

  const [{ directory, matchedTable }] = matchedPairs;
  const html = matchedTable?.html || '';

  if (typeof setSelectedDirectory === 'function') {
    setSelectedDirectory(directory);
  }

  if (typeof setTableHtml === 'function') {
    setTableHtml(html);
  }

  return directory && html ? STEP.CONFIG : STEP.SELECT_DIRECTORY;
};

export const handleFileChange = (e, setFile, setError) => {
  // 处理上传文件变更并清空错误状态。
  const selectedFile = e.target.files[0];
  if (selectedFile) {
    setFile(selectedFile);
    setError('');
  }
};

export const handleExtractDirectory = async (
  file,
  documentType,
  setLoading,
  setError,
  setDirectoryInfo,
  setAvailableTables,
  setCurrentStep,
  setSelectedDirectory,
  setTableHtml,
) => {
  // 上传整份车辆文档并提取目录和候选表格。
  setLoading(true);
  setError('');

  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', documentType);

  try {
    const { response, data } = await postFormData('/api/car-extract-directory', formData);

    if (response.ok && data.success) {
      const directories = Array.isArray(data.directory_info) ? data.directory_info : [];
      const tables = Array.isArray(data.tables) ? data.tables : [];

      setDirectoryInfo(directories);
      setAvailableTables(tables);

      const nextStep = resolveNextStepBySingleMatch(
        directories,
        tables,
        setSelectedDirectory,
        setTableHtml
      );
      setCurrentStep(nextStep);
      return;
    }

    setError(data?.error || '目录提取失败');
    setCurrentStep(STEP.UPLOAD);
  } catch (err) {
    setError('网络错误: ' + err.message);
    setCurrentStep(STEP.UPLOAD);
  } finally {
    setLoading(false);
  }
};

export const handleDirectorySelect = (
  e,
  setSelectedDirectory,
  availableTables,
  setTableHtml,
  setError
) => {
  // 根据目录下拉选项回填匹配到的HTML表格。
  const directory = e.target.value;
  setSelectedDirectory(directory);
  setError('');

  if (!directory) {
    setTableHtml('');
    return;
  }

  const matchedTable = findMatchedTableByDirectory(directory, availableTables);

  setTableHtml(matchedTable ? matchedTable.html : '');

  if (!matchedTable) {
    setError('未找到与该目录匹配的表格');
  }
};

export const handleExtractTable = async (
  tableHtml,
  selectedLevel,
  documentType,
  setLoading,
  setError,
  setTables,
  setCurrentStep,
  setLevelResults,
  setSelectedLevel,
  manageLoading = true,
  recordTask = true,
  parentTaskId = null
) => {
   // 调用后端提取表格数据并维护等级结果回退逻辑。
  if (!tableHtml) {
    setError('请选择目录以获取表格');
    return;
  }

  if (manageLoading) {
    setLoading(true);
  }
  setError('');
  setTables([]);

  try {
    const { response, data } = await postJson('/api/car-get-table-data', {
      table_html: tableHtml,
      level: selectedLevel,
      all_levels: true,
      document_type: documentType,
      record_task: recordTask,
      task_id: parentTaskId,
    });

    if (!response.ok || !data?.success) {
      setError(data?.error || '表格提取失败');
      setCurrentStep(STEP.SELECT_DIRECTORY);
      return;
    }

    const levelResults = data.level_results || {};
    if (typeof setLevelResults === 'function') {
      const normalizedMap = Object.entries(levelResults).reduce((acc, [level, tables]) => {
        acc[level] = normalizeTables(tables);
        return acc;
      }, {});

      let activeLevel = selectedLevel;
      if (!hasRows(normalizedMap[activeLevel])) {
        const fallbackLevel = Object.keys(normalizedMap)
          .find((level) => hasRows(normalizedMap[level]));

        if (fallbackLevel) {
          activeLevel = fallbackLevel;
          if (typeof setSelectedLevel === 'function') {
            setSelectedLevel(fallbackLevel);
          }
        }
      }
      const fallbackResultTables = normalizeTables(Array.isArray(data?.result) ? data.result : []);
      const hasAnyLevelRows = Object.values(normalizedMap).some((tables) => hasRows(tables));
      const finalMap = hasAnyLevelRows
        ? normalizedMap
        : (fallbackResultTables.length ? { [NO_LEVEL_KEY]: fallbackResultTables } : normalizedMap);
      const finalActiveLevel = hasAnyLevelRows ? activeLevel : (fallbackResultTables.length ? NO_LEVEL_KEY : activeLevel);

      if (finalActiveLevel !== activeLevel && typeof setSelectedLevel === 'function') {
        setSelectedLevel(finalActiveLevel);
      }

      setLevelResults(finalMap);
      setTables(finalMap[finalActiveLevel] || []);
    } else {
      const tables = Array.isArray(data?.result) ? data.result : [];
      setTables(normalizeTables(tables));
    }
    setCurrentStep(STEP.RESULTS);
  } catch (err) {
    setError('网络错误: ' + err.message);
    setCurrentStep(STEP.SELECT_DIRECTORY);
  } finally {
    if (manageLoading) {
      setLoading(false);
    }
  }
};

export const getLoadingMessage = (documentType, deviceType, step) => {
   // 根据处理模式与步骤生成统一加载文案。
  if (documentType === DOCUMENT_TYPE.DEVICE) {
    const deviceMap = {
      preamplifier: '有源放大器',
      aimn: '人工电源网络',
      pulse_limiter: '同轴线缆/衰减器',
      receiving_antenna: '接收天线',
      probe: '探头',
    };
    return deviceType ? `正在处理${deviceMap[deviceType] || '设备'}数据...` : '请先选择设备类型';
  }

  if (step === 'extract-directory') return '正在提取目录信息...';
  if (step === STEP.CONFIG || step === STEP.RESULTS) {
    return documentType === DOCUMENT_TYPE.ENTERPRISE
      ? '正在调用AI提取企业标准表格，请稍候...'
      : '正在提取全部等级表格数据...';
  }
  return '准备处理车辆EMC测试报告...';
};